/**
 * FinalizeGateModal — Dialog shown when pipeline execution is paused
 * at a finalization gate due to unresolved issues.
 *
 * Shows per-step status with resolution buttons for warning steps.
 * "Finalize" button disabled until all issues resolved.
 * Triggered by `pipeline_paused` SSE event.
 */
import React from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import {
  CheckCircle as PassIcon,
  WarningAmber as WarnIcon,
  Error as FailIcon,
  AutoFixHigh as FixIcon,
  GppGood as FinalizeIcon,
  Replay as RetryIcon,
} from '@mui/icons-material';

interface GateIssue {
  stepId: string;
  stepTitle: string;
  severity: 'warning' | 'error';
  message: string;
  canAutoFix: boolean;
}

interface FinalizeGateModalProps {
  open: boolean;
  onClose: () => void;
  issues: GateIssue[];
  onResolve: (stepId: string, action: 'auto_fix' | 'skip' | 'retry') => void;
  onFinalize: () => void;
  resolvedStepIds: Set<string>;
}

const FinalizeGateModal: React.FC<FinalizeGateModalProps> = ({
  open,
  onClose,
  issues,
  onResolve,
  onFinalize,
  resolvedStepIds,
}) => {
  const theme = useTheme();
  const unresolvedCount = issues.filter((i) => !resolvedStepIds.has(i.stepId)).length;
  const canFinalize = unresolvedCount === 0;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <FinalizeIcon sx={{ color: 'warning.main' }} />
          <Typography variant="h6" fontWeight={600}>
            Pipeline Finalization
          </Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {unresolvedCount > 0
            ? `${unresolvedCount} issue${unresolvedCount !== 1 ? 's' : ''} need${unresolvedCount === 1 ? 's' : ''} resolution before finalizing.`
            : 'All issues resolved. Ready to finalize.'}
        </Typography>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ px: 2, py: 1 }}>
        <List disablePadding>
          {issues.map((issue) => {
            const isResolved = resolvedStepIds.has(issue.stepId);
            const isError = issue.severity === 'error';

            return (
              <ListItem
                key={issue.stepId}
                sx={{
                  px: 1.5,
                  py: 1,
                  mb: 0.5,
                  borderRadius: 1.5,
                  bgcolor: isResolved
                    ? alpha(theme.palette.success.main, 0.04)
                    : isError
                      ? alpha(theme.palette.error.main, 0.04)
                      : alpha(theme.palette.warning.main, 0.04),
                  border: `1px solid ${
                    isResolved
                      ? alpha(theme.palette.success.main, 0.2)
                      : isError
                        ? alpha(theme.palette.error.main, 0.2)
                        : alpha(theme.palette.warning.main, 0.2)
                  }`,
                }}
              >
                <ListItemIcon sx={{ minWidth: 32 }}>
                  {isResolved ? (
                    <PassIcon sx={{ fontSize: 20, color: 'success.main' }} />
                  ) : isError ? (
                    <FailIcon sx={{ fontSize: 20, color: 'error.main' }} />
                  ) : (
                    <WarnIcon sx={{ fontSize: 20, color: 'warning.main' }} />
                  )}
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Typography variant="body2" fontWeight={500}>
                      {issue.stepTitle}
                    </Typography>
                  }
                  secondary={
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11 }}>
                      {issue.message}
                    </Typography>
                  }
                />
                {!isResolved && (
                  <Stack direction="row" spacing={0.5}>
                    {issue.canAutoFix && (
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<FixIcon sx={{ fontSize: 14 }} />}
                        onClick={() => onResolve(issue.stepId, 'auto_fix')}
                        sx={{ fontSize: 10, textTransform: 'none', minWidth: 0, px: 1 }}
                      >
                        Fix
                      </Button>
                    )}
                    <Button
                      size="small"
                      variant="outlined"
                      color="secondary"
                      startIcon={<RetryIcon sx={{ fontSize: 14 }} />}
                      onClick={() => onResolve(issue.stepId, 'retry')}
                      sx={{ fontSize: 10, textTransform: 'none', minWidth: 0, px: 1 }}
                    >
                      Retry
                    </Button>
                    <Button
                      size="small"
                      variant="text"
                      color="inherit"
                      onClick={() => onResolve(issue.stepId, 'skip')}
                      sx={{ fontSize: 10, textTransform: 'none', minWidth: 0, px: 1, color: 'text.secondary' }}
                    >
                      Skip
                    </Button>
                  </Stack>
                )}
                {isResolved && (
                  <Chip
                    label="Resolved"
                    size="small"
                    color="success"
                    variant="outlined"
                    sx={{ fontSize: 10, height: 20 }}
                  />
                )}
              </ListItem>
            );
          })}
        </List>
      </DialogContent>

      <Divider />

      <DialogActions sx={{ px: 2, py: 1.5 }}>
        <Button onClick={onClose} color="inherit" sx={{ textTransform: 'none' }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="success"
          disabled={!canFinalize}
          onClick={onFinalize}
          startIcon={<FinalizeIcon sx={{ fontSize: 18 }} />}
          sx={{ textTransform: 'none', fontWeight: 600 }}
        >
          Finalize Pipeline
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FinalizeGateModal;
